/**
 * Main Application Component
 * Capital Management System
 * Features dynamic theme selection based on user email domain
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, CircularProgress, Box } from '@mui/material';
import { PermissionProvider, usePermissions } from './contexts/PermissionContext';
import { TenantProvider, useTenant } from './contexts/TenantContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { MODULES, PERMISSION_LEVELS } from './contexts/PermissionConstants';
import ProtectedRoute from './components/common/ProtectedRoute';
import RateLimitToastBridge from './components/common/RateLimitToastBridge';
import MainLayout from './components/layout/MainLayout';
import { generateThemeFromTenantConfig } from './utils/themeGenerator';

// Import default theme (fallback)
import defaultTheme from './theme/theme';

// Import pages
import LoginPage from './pages/LoginPage';
import PendingApprovalPage from './pages/PendingApprovalPage';
import NoAccessPage from './pages/NoAccessPage';
import Dashboard from './pages/Dashboard';
import ContractList from './pages/ContractList';
import ContractCreate from './pages/ContractCreate';
import ContractEdit from './pages/ContractEdit';
import ContractDetail from './pages/ContractDetail';
import PaymentMatching from './pages/PaymentMatching';
import StakeholderManagement from './pages/StakeholderManagement';
import ReferenceDataManagement from './pages/ReferenceDataManagement';
import ReportsPage from './pages/ReportsPage';
import UserManagement from './pages/UserManagement';
import ProfileSettings from './pages/ProfileSettings';
import PendingUsersPage from './pages/admin/PendingUsersPage';
import TenantManagement from './pages/admin/TenantManagement';
import RoleManagement from './pages/admin/RoleManagement';
import EmailAuditPage from './pages/admin/EmailAuditPage';
import EmailSettingsPage from './pages/admin/EmailSettingsPage';
import EmailRecipientsPage from './pages/admin/EmailRecipientsPage';
import EmailTemplatesPage from './pages/admin/EmailTemplatesPage';
import PDFTemplatesPage from './pages/admin/PDFTemplatesPage';
import TenantSettingsPage from './pages/admin/TenantSettingsPage';
import ServiceStatusPage from './pages/admin/ServiceStatusPage';
import ApiSettingsPage from './pages/admin/ApiSettingsPage';
import WorkflowDocumentRequirementsPage from './pages/admin/WorkflowDocumentRequirementsPage';
import CalculationProfilesPage from './pages/admin/CalculationProfilesPage';
import DocumentManagementPage from './pages/admin/DocumentManagementPage';
import MergeContractsPage from './pages/admin/MergeContractsPage';
import ApprovalsPage from './pages/ApprovalsPage';
import KeycloakCallback from './pages/KeycloakCallback';
import RateChangeImpact from './pages/RateChangeImpact';

// Client Portal pages
import ClientLayout from './components/layout/ClientLayout';
import ClientDashboard from './pages/client-portal/ClientDashboard';
import ClientContracts from './pages/client-portal/ClientContracts';
import ClientContractDetail from './pages/client-portal/ClientContractDetail';
import ClientDocuments from './pages/client-portal/ClientDocuments';

const Unauthorized = () => (
  <div style={{ textAlign: 'center', padding: '2rem' }}>
    <h1>Access Denied</h1>
    <p>You do not have permission to access this page.</p>
  </div>
);

/**
 * Master Tenant Home Redirect Component
 * Redirects master/default tenant users to Service Status page (if they have admin access)
 * Regular tenant users see the normal Dashboard
 */
const MasterTenantHomeRedirect = () => {
  const { tenant, loading: tenantLoading } = useTenant();
  const { isAdmin, hasPermission, loading: permLoading } = usePermissions();

  if (tenantLoading || permLoading) {
    return <LoadingScreen />;
  }

  // Server-authoritative flag (see the API's requireOperatingTenant middleware) so an
  // unresolved tenant can never redirect an operating-tenant admin off the Dashboard.
  const isMasterTenant = tenant?.isMaster === true;

  // Only redirect to admin page if user has admin permissions
  if (isMasterTenant && isAdmin) {
    return <Navigate to="/admin/service-status" replace />;
  }

  // If user has capital_management read, show Dashboard
  if (hasPermission(MODULES.CAPITAL_MANAGEMENT, PERMISSION_LEVELS.READ)) {
    return <Dashboard />;
  }

  // Otherwise redirect to the first permitted page
  const fallbackRoutes = [
    { path: '/contracts', module: MODULES.CONTRACT_MANAGEMENT, level: PERMISSION_LEVELS.READ },
    { path: '/payments', module: MODULES.PAYMENTS, level: PERMISSION_LEVELS.WRITE },
    { path: '/stakeholders', module: MODULES.STAKEHOLDER_MANAGEMENT, level: PERMISSION_LEVELS.READ },
    { path: '/reports', module: MODULES.REPORTS, level: PERMISSION_LEVELS.READ },
  ];

  for (const route of fallbackRoutes) {
    if (hasPermission(route.module, route.level)) {
      return <Navigate to={route.path} replace />;
    }
  }

  return <Navigate to="/unauthorized" replace />;
};

/**
 * Loading Screen Component
 */
const LoadingScreen = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      flexDirection: 'column',
      gap: 2,
    }}
  >
    <CircularProgress />
    <div>Loading...</div>
  </Box>
);

/**
 * ThemedApp Component
 * Wraps the app with dynamic theme based on user context
 */
const ThemedApp = () => {
  const { userTheme, themePreference, isAuthenticated, hasNoTenantAccess, loading, login, loginAsExternal, user } = usePermissions();
  const { tenant } = useTenant();
  const isExternalUser = user?.userType === 'external_client';

  // "Auto" (no explicit preference) must track the tenant's live theme rather than the
  // snapshot PermissionContext captured at login time — TenantContext's config fetch
  // resolves independently and often later, so userTheme alone goes stale on first load.
  const tenantTheme = tenant?.theme?.primaryColor ? generateThemeFromTenantConfig(tenant.theme) : null;
  const activeTheme = (!themePreference && tenantTheme) || userTheme || defaultTheme;

  // Preserve tenant query parameter when redirecting to login
  const getLoginRedirect = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const tenant = searchParams.get('tenant');
    return tenant ? `/login?tenant=${tenant}` : '/login';
  };

  if (loading) {
    return (
      <ThemeProvider theme={activeTheme}>
        <CssBaseline />
        <LoadingScreen />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={activeTheme}>
      <CssBaseline />
      <Router>
        <NotificationProvider>
        <RateLimitToastBridge />
        <Routes>
          {/* Public routes */}
          <Route
            path="/login"
            element={
              isAuthenticated && hasNoTenantAccess ? (
                <Navigate to="/no-access" replace />
              ) : isAuthenticated ? (
                <Navigate to="/" replace />
              ) : (
                <LoginPage onLogin={login} onKeycloakLogin={loginAsExternal} />
              )
            }
          />
          <Route path="/unauthorized" element={<Unauthorized />} />
          <Route path="/auth/keycloak/callback" element={<KeycloakCallback />} />
          <Route path="/pending-approval" element={<PendingApprovalPage />} />
          <Route
            path="/no-access"
            element={
              isAuthenticated ? (
                <NoAccessPage />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />

          {/* Client Portal routes for external users */}
          <Route
            path="/client-portal/*"
            element={
              isAuthenticated && isExternalUser ? (
                <ClientLayout>
                  <Routes>
                    <Route path="/" element={<ClientDashboard />} />
                    <Route path="/contracts" element={<ClientContracts />} />
                    <Route path="/contracts/:id" element={<ClientContractDetail />} />
                    <Route path="/documents" element={<ClientDocuments />} />
                    <Route path="*" element={<Navigate to="/client-portal" replace />} />
                  </Routes>
                </ClientLayout>
              ) : isAuthenticated ? (
                <Navigate to="/" replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />

          {/* Protected routes - redirect to login if not authenticated */}
          {/* Users with no tenant access are redirected to no-access page */}
          {/* External users are redirected to client portal */}
          <Route
            path="/*"
            element={
              isAuthenticated && hasNoTenantAccess ? (
                <Navigate to="/no-access" replace />
              ) : isAuthenticated && isExternalUser ? (
                <Navigate to="/client-portal" replace />
              ) : isAuthenticated ? (
                <MainLayout>
                  <Routes>
                    {/* Dashboard - Master tenant redirects to Service Status */}
                    <Route
                      path="/"
                      element={
                        <ProtectedRoute module={MODULES.CAPITAL_MANAGEMENT} level={PERMISSION_LEVELS.READ}>
                          <MasterTenantHomeRedirect />
                        </ProtectedRoute>
                      }
                    />

                    {/* Contracts */}
                    <Route
                      path="/contracts"
                      element={
                        <ProtectedRoute module={MODULES.CONTRACT_MANAGEMENT} level={PERMISSION_LEVELS.READ}>
                          <ContractList />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/contracts/new"
                      element={
                        <ProtectedRoute module={MODULES.CONTRACT_MANAGEMENT} level={PERMISSION_LEVELS.WRITE}>
                          <ContractCreate />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/contracts/:id/edit"
                      element={
                        <ProtectedRoute module={MODULES.CONTRACT_MANAGEMENT} level={PERMISSION_LEVELS.WRITE}>
                          <ContractEdit />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/contracts/:id"
                      element={
                        <ProtectedRoute module={MODULES.CONTRACT_MANAGEMENT} level={PERMISSION_LEVELS.READ}>
                          <ContractDetail />
                        </ProtectedRoute>
                      }
                    />

                    {/* Matching */}
                    <Route
                      path="/payments"
                      element={
                        <ProtectedRoute module={MODULES.PAYMENTS} level={PERMISSION_LEVELS.WRITE}>
                          <PaymentMatching />
                        </ProtectedRoute>
                      }
                    />

                    {/* Approvals Dashboard */}
                    <Route
                      path="/approvals"
                      element={
                        <ProtectedRoute module={MODULES.CONTRACT_MANAGEMENT} level={PERMISSION_LEVELS.READ}>
                          <ApprovalsPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Stakeholders */}
                    <Route
                      path="/stakeholders/*"
                      element={
                        <ProtectedRoute module={MODULES.STAKEHOLDER_MANAGEMENT} level={PERMISSION_LEVELS.READ}>
                          <StakeholderManagement />
                        </ProtectedRoute>
                      }
                    />

                    {/* Reference Data */}
                    <Route
                      path="/reference-data/*"
                      element={
                        <ProtectedRoute module={MODULES.REFERENCE_DATA} level={PERMISSION_LEVELS.READ}>
                          <ReferenceDataManagement />
                        </ProtectedRoute>
                      }
                    />

                    {/* Rate Change Impact Assessment */}
                    <Route
                      path="/rate-changes/impact"
                      element={
                        <ProtectedRoute module={MODULES.CONTRACT_MANAGEMENT} level={PERMISSION_LEVELS.READ}>
                          <RateChangeImpact />
                        </ProtectedRoute>
                      }
                    />

                    {/* Reports */}
                    <Route
                      path="/reports/*"
                      element={
                        <ProtectedRoute module={MODULES.REPORTS} level={PERMISSION_LEVELS.READ}>
                          <ReportsPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* User Management */}
                    <Route
                      path="/users"
                      element={
                        <ProtectedRoute module={MODULES.USER_MANAGEMENT} level={PERMISSION_LEVELS.ADMIN}>
                          <UserManagement />
                        </ProtectedRoute>
                      }
                    />

                    {/* Administration */}
                    <Route
                      path="/admin/pending-users"
                      element={
                        <ProtectedRoute module={MODULES.USER_MANAGEMENT} level={PERMISSION_LEVELS.ADMIN}>
                          <PendingUsersPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/email-settings"
                      element={
                        <ProtectedRoute module={MODULES.ADMIN_SETTINGS} level={PERMISSION_LEVELS.ADMIN}>
                          <EmailSettingsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/email-recipients"
                      element={
                        <ProtectedRoute module={MODULES.ADMIN_SETTINGS} level={PERMISSION_LEVELS.ADMIN}>
                          <EmailRecipientsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/email-audit"
                      element={
                        <ProtectedRoute module={MODULES.ADMIN_SETTINGS} level={PERMISSION_LEVELS.ADMIN}>
                          <EmailAuditPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/email-templates"
                      element={
                        <ProtectedRoute module={MODULES.ADMIN_SETTINGS} level={PERMISSION_LEVELS.ADMIN}>
                          <EmailTemplatesPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/pdf-templates"
                      element={
                        <ProtectedRoute module={MODULES.ADMIN_SETTINGS} level={PERMISSION_LEVELS.ADMIN}>
                          <PDFTemplatesPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/settings"
                      element={
                        <ProtectedRoute module={MODULES.ADMIN_SETTINGS} level={PERMISSION_LEVELS.ADMIN}>
                          <TenantSettingsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/merge-contracts"
                      element={
                        <ProtectedRoute module={MODULES.CONTRACT_MANAGEMENT} level={PERMISSION_LEVELS.ADMIN}>
                          <MergeContractsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/tenants"
                      element={
                        <ProtectedRoute module={MODULES.TENANT_MANAGEMENT} level={PERMISSION_LEVELS.ADMIN}>
                          <TenantManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/roles"
                      element={
                        <ProtectedRoute module={MODULES.USER_MANAGEMENT} level={PERMISSION_LEVELS.ADMIN}>
                          <RoleManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/service-status"
                      element={
                        <ProtectedRoute module={MODULES.ADMIN_SETTINGS} level={PERMISSION_LEVELS.ADMIN}>
                          <ServiceStatusPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/api-settings"
                      element={
                        <ProtectedRoute module={MODULES.ADMIN_SETTINGS} level={PERMISSION_LEVELS.ADMIN}>
                          <ApiSettingsPage />
                        </ProtectedRoute>
                      }
                    />
                    {/* Document Types moved into Reference Data (T3-039). Redirect
                        any bookmarks/links to the Reference Data screen. */}
                    <Route
                      path="/admin/document-types"
                      element={<Navigate to="/reference-data" replace />}
                    />
                    <Route
                      path="/admin/workflow-document-requirements"
                      element={
                        <ProtectedRoute module={MODULES.WORKFLOW_DOCUMENT_REQUIREMENTS} level={PERMISSION_LEVELS.READ}>
                          <WorkflowDocumentRequirementsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/calculation-profiles"
                      element={
                        <ProtectedRoute module={MODULES.REFERENCE_DATA} level={PERMISSION_LEVELS.ADMIN}>
                          <CalculationProfilesPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/documents"
                      element={
                        <ProtectedRoute module={MODULES.CONTRACT_MANAGEMENT} level={PERMISSION_LEVELS.WRITE}>
                          <DocumentManagementPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Profile Settings */}
                    <Route
                      path="/settings"
                      element={
                        <ProtectedRoute module={MODULES.CAPITAL_MANAGEMENT} level={PERMISSION_LEVELS.READ}>
                          <ProfileSettings />
                        </ProtectedRoute>
                      }
                    />

                    {/* Default redirect */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </MainLayout>
              ) : (
                <Navigate to={getLoginRedirect()} replace />
              )
            }
          />
        </Routes>
        </NotificationProvider>
      </Router>
    </ThemeProvider>
  );
};

/**
 * Main App Component
 * Wraps ThemedApp with PermissionProvider and TenantProvider
 */
function App() {
  return (
    <PermissionProvider>
      <TenantProvider>
        <ThemedApp />
      </TenantProvider>
    </PermissionProvider>
  );
}

export default App;
